import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, Navigate, useLocation, useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import FeedbackButton from '../common/FeedbackButton';
import Logo from '../common/Logo';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const NOTIF_ICONS = {
  invite_received: '📨',
  invite_accepted: '🎉',
  cross_tree_request: '🔗',
  cross_tree_accepted: '✅',
  member_added: '👤',
};

const AppLayout = () => {
  const { user, loading, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileNotifs, setMobileNotifs] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const skipNextFetch = React.useRef(false);

  const fetchNotifications = useCallback(() => {
    if (skipNextFetch.current) { skipNextFetch.current = false; return; }
    api.getNotifications().then(res => {
      setNotifications(res.notifications || []);
      setUnreadCount(res.unreadCount || 0);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [user, fetchNotifications]);

  useEffect(() => { if (user) fetchNotifications(); }, [location, user, fetchNotifications]);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) setSidebarOpen(true);
      else setSidebarOpen(false);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    if (isMobile) setSidebarOpen(false);
  }, [location, isMobile]);

  const handleMarkAllRead = async () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
    skipNextFetch.current = true;
    await api.markAllNotificationsRead().catch(() => {});
    fetchNotifications();
  };

  const handleNotifClick = async (notif) => {
    setNotifications(prev => prev.map(n => n._id === notif._id ? { ...n, read: true } : n));
    if (!notif.read) setUnreadCount(prev => Math.max(0, prev - 1));
    skipNextFetch.current = true;
    await api.markNotificationRead(notif._id).catch(() => {});
    setMobileNotifs(false);
    let dest = '/family';
    let state = { _t: Date.now() };
    if (notif.type === 'cross_tree_request' || notif.type === 'cross_tree_accepted') {
      state.scrollTo = 'cross-tree-requests';
      state.linkId = notif.data?.linkId;
    } else if (notif.type === 'invite_received') {
      state.scrollTo = 'members';
      state.tab = 'pending';
    } else if (notif.type === 'invite_accepted' || notif.type === 'member_added') {
      state.scrollTo = 'members';
      state.personId = notif.data?.personId;
    }
    if (notif.data?.treeId) state.treeId = notif.data.treeId;
    navigate(dest, { state, replace: location.pathname === dest });
  };

  const timeAgo = (date) => {
    const diff = Date.now() - new Date(date).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexDirection: 'column', gap: 16, background: 'var(--bg-primary)',
      }}>
        <Logo size={48} />
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: 'var(--accent-green)' }}>LegacyRoots</div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading...</div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" />;

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--bg-primary)' }}>
      <Sidebar isOpen={sidebarOpen || !isMobile} onToggle={() => setSidebarOpen(!sidebarOpen)} />

      <div style={{ flex: 1, marginLeft: isMobile ? 0 : 240, minHeight: '100vh' }}>
        {/* Mobile header with hamburger + notification bell */}
        {isMobile && (
          <header style={{
            position: 'sticky', top: 0, zIndex: 40,
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '12px 16px',
            background: 'var(--bg-sidebar)',
            borderBottom: '1px solid var(--border)',
          }}>
            <button onClick={() => setSidebarOpen(true)} style={{
              width: 36, height: 36, borderRadius: 10,
              background: 'var(--bg-secondary)', border: '1px solid var(--border)',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              gap: 4, cursor: 'pointer', padding: 8,
            }}>
              <div style={{ width: 16, height: 2, background: 'var(--text-muted)', borderRadius: 1 }} />
              <div style={{ width: 16, height: 2, background: 'var(--text-muted)', borderRadius: 1 }} />
              <div style={{ width: 16, height: 2, background: 'var(--text-muted)', borderRadius: 1 }} />
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
              <Logo size={28} />
              <span style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>LegacyRoots</span>
            </div>

            {/* Mobile notification bell */}
            <div style={{ position: 'relative' }}>
              <button onClick={() => {
                const opening = !mobileNotifs;
                setMobileNotifs(opening);
                if (opening && unreadCount > 0) handleMarkAllRead();
              }} style={{
                width: 36, height: 36, borderRadius: 10,
                background: mobileNotifs ? 'var(--accent-green-light)' : 'var(--bg-secondary)',
                border: '1px solid var(--border)', cursor: 'pointer', fontSize: 16,
                display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative',
              }}>
                🔔
                {unreadCount > 0 && (
                  <span style={{
                    position: 'absolute', top: -4, right: -4, minWidth: 18, height: 18,
                    borderRadius: 9, background: '#E74C3C', color: '#FFF',
                    fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: '0 4px', border: '2px solid var(--bg-sidebar)',
                  }}>{unreadCount > 9 ? '9+' : unreadCount}</span>
                )}
              </button>
            </div>

            {/* Mobile logout button */}
            <button onClick={async () => { await logout(); navigate('/'); }} style={{
              width: 36, height: 36, borderRadius: 10,
              background: 'var(--bg-secondary)', border: '1px solid var(--border)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', fontSize: 14, color: 'var(--text-muted)',
            }} title="Sign Out">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
            </button>
          </header>
        )}

        {/* Mobile notification dropdown */}
        {isMobile && mobileNotifs && (
          <>
            <div onClick={() => setMobileNotifs(false)} style={{ position: 'fixed', inset: 0, zIndex: 41 }} />
            <div style={{
              position: 'fixed', top: 60, right: 12, width: 'min(340px, 90vw)',
              maxHeight: 400, overflowY: 'auto',
              borderRadius: 16, background: 'var(--bg-card)', border: '1px solid var(--border)',
              boxShadow: 'var(--shadow-lg)', zIndex: 42,
            }}>
              <div style={{
                padding: '14px 16px 10px', borderBottom: '1px solid var(--border)',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <span style={{ fontSize: 15, fontWeight: 700, fontFamily: "'Cormorant Garamond',serif", color: 'var(--text-primary)' }}>Notifications</span>
                {unreadCount > 0 && (
                  <button onClick={handleMarkAllRead} style={{
                    background: 'none', border: 'none', color: 'var(--accent-green)',
                    fontSize: 11, fontWeight: 600, cursor: 'pointer',
                  }}>Mark all read</button>
                )}
              </div>
              {notifications.length === 0 ? (
                <div style={{ padding: '24px 16px', textAlign: 'center', color: 'var(--text-dim)', fontSize: 13 }}>
                  No notifications yet
                </div>
              ) : (
                notifications.map(notif => (
                  <div
                    key={notif._id}
                    onClick={() => handleNotifClick(notif)}
                    style={{
                      padding: '12px 16px', cursor: 'pointer',
                      borderBottom: '1px solid var(--border)',
                      background: notif.read ? 'transparent' : 'var(--accent-green-light)',
                    }}
                  >
                    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                      <span style={{ fontSize: 18, flexShrink: 0, marginTop: 2 }}>{NOTIF_ICONS[notif.type] || '🔔'}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: notif.read ? 400 : 600, color: 'var(--text-primary)', marginBottom: 2 }}>{notif.title}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.4 }}>{notif.message}</div>
                        <div style={{ fontSize: 10, color: 'var(--text-dim)', marginTop: 4 }}>{timeAgo(notif.createdAt)}</div>
                      </div>
                      {!notif.read && (
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--accent-green)', flexShrink: 0, marginTop: 6 }} />
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}

        <main style={{ padding: isMobile ? '16px 12px' : '28px', maxWidth: 900, margin: '0 auto' }}>
          <Outlet />
        </main>
      </div>
      <FeedbackButton />
    </div>
  );
};

export default AppLayout;
